import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, 
  Activity, 
  RefreshCcw, 
  ChevronRight, 
  PlusCircle, 
  ShieldCheck, 
  Monitor, 
  Database, 
  Globe,
  Terminal,
  Settings,
  MoreVertical,
  History,
  AlertCircle,
  CheckCircle2,
  Cpu,
  Layers,
  Search,
  ExternalLink,
  ArrowUpRight,
  ArrowDownLeft,
  X
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Styled Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> = 
  ({ label, value, icon, color, subText }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-2xl font-bold text-white tracking-tight">{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

// ============== Interfaces ==============
interface EMSXOrder {
  id: number;
  emsxSequence: number;
  internalOrderId: string;
  side: 'BUY' | 'SELL' | 'SHRT' | 'COVR';
  ticker: string;
  cusip: string;
  securityName: string;
  amount: number;
  filledAmount: number;
  remainingAmount: number;
  orderType: 'MARKET' | 'LIMIT' | 'STOP' | 'STOP_LIMIT' | 'MOC' | 'LOC' | 'VWAP' | 'TWAP';
  limitPrice?: number;
  stopPrice?: number;
  status: string;
  broker: string;
  route: string;
  strategy?: string;
  timeInForce: string;
  account: string;
  portfolio: string;
  avgFillPrice?: number;
  lastFillPrice?: number;
  lastFillQty?: number;
  createdAt: string;
  updatedAt: string;
  sentAt?: string;
  filledAt?: string;
  notes?: string;
}

const EMSXIntegrationScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'orders' | 'executions' | 'routes' | 'config'>('orders');
  const [orders, setOrders] = useState<EMSXOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/emsx/orders');
      setOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('API not available, using mock data');
      setOrders([
        { id: 1, emsxSequence: 1001, internalOrderId: 'ORD-001', side: 'BUY', ticker: 'AAPL', cusip: '037833100', securityName: 'Apple Inc.', amount: 10000, filledAmount: 7500, remainingAmount: 2500, orderType: 'LIMIT', limitPrice: 195.50, status: 'WORKING', broker: 'GSCO', route: 'SIGMA', timeInForce: 'DAY', account: 'MAIN-001', portfolio: 'Global Growth', avgFillPrice: 195.32, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
        { id: 2, emsxSequence: 1002, internalOrderId: 'ORD-002', side: 'SELL', ticker: 'MSFT', cusip: '594918104', securityName: 'Microsoft Corp.', amount: 5000, filledAmount: 5000, remainingAmount: 0, orderType: 'MARKET', status: 'FILLED', broker: 'MLCO', route: 'INSTINCT', timeInForce: 'DAY', account: 'MAIN-001', portfolio: 'US Equity', avgFillPrice: 378.45, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredOrders = useMemo(() => {
    if (!searchTerm) return orders;
    const s = searchTerm.toLowerCase();
    return orders.filter(o => 
      o.ticker.toLowerCase().includes(s) || 
      o.securityName.toLowerCase().includes(s) ||
      o.internalOrderId.toLowerCase().includes(s)
    );
  }, [orders, searchTerm]);

  const columnDefs = useMemo<ColDef[]>(() => [
    { field: 'status', headerName: 'Status', width: 130, cellRenderer: (p: any) => (
      <span className={cn(
        "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
        p.value === 'FILLED' ? "bg-emerald-500/10 text-emerald-400" : 
        p.value === 'WORKING' ? "bg-blue-500/10 text-blue-400" :
        "bg-slate-800 text-slate-400"
      )}>
        {p.value}
      </span>
    )},
    { field: 'side', headerName: 'Side', width: 80, cellRenderer: (p: any) => (
      <span className={cn("font-bold", p.value === 'BUY' ? "text-emerald-400" : "text-red-400")}>
        {p.value}
      </span>
    )},
    { field: 'ticker', headerName: 'Ticker', width: 90, cellStyle: { fontWeight: 'bold' } as any },
    { field: 'securityName', headerName: 'Security', flex: 1, cellStyle: { color: '#94a3b8' } as any },
    { field: 'amount', headerName: 'Qty', width: 100, valueFormatter: (p) => p.value?.toLocaleString(), cellStyle: { textAlign: 'right', fontFamily: 'monospace' } as any },
    { field: 'filledAmount', headerName: 'Filled', width: 100, valueFormatter: (p) => p.value?.toLocaleString(), cellStyle: { textAlign: 'right', fontFamily: 'monospace', color: '#10b981' } as any },
    { field: 'avgFillPrice', headerName: 'Avg Px', width: 100, valueFormatter: (p) => p.value ? `$${p.value.toFixed(2)}` : '-', cellStyle: { textAlign: 'right', fontWeight: 'bold' } as any },
    { field: 'broker', headerName: 'Broker', width: 90 },
    { field: 'route', headerName: 'Route', width: 100 },
    { field: 'portfolio', headerName: 'Portfolio', width: 150, cellStyle: { color: '#64748b' } as any }
  ], []);

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <div className="px-3 py-1 bg-gradient-to-r from-orange-500 to-amber-600 rounded-md text-xs font-black text-white tracking-tighter">BLOOMBERG</div>
                EMSX Integration
              </h1>
              <p className="text-slate-500 font-medium mt-1">Institutional execution management and direct market access gateway</p>
            </div>
            <div className="flex gap-3">
              <div className="flex items-center gap-3 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                <div className={cn("w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]", isConnected && "animate-pulse")} />
                <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest">EMSX Connected</span>
              </div>
              <button 
                onClick={fetchData}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Sync
              </button>
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-xl text-sm font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 transition-all">
                <PlusCircle className="w-4 h-4" />
                New Order
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Live Orders" value={String(orders.length)} icon={<Terminal className="w-5 h-5" />} color="#f59e0b" subText="Working in EMSX" />
            <StatCard label="Filled Today" value={String(orders.filter(o => o.status === 'FILLED').length)} icon={<CheckCircle2 className="w-5 h-5" />} color="#10b981" subText="Completed executions" />
            <StatCard label="Gateway Latency" value="12ms" icon={<Zap className="w-5 h-5" />} color="#3b82f6" subText="P99 Roundtrip" />
            <StatCard label="Fill Rate" value="84.2%" icon={<Activity className="w-5 h-5" />} color="#8b5cf6" subText="Volume weighted" />
          </div>
        </motion.div>

        <div className="flex items-center border-b border-white/5 mb-6">
          {[
            { id: 'orders', label: 'Order Blotter', icon: <Layers className="w-4 h-4" /> },
            { id: 'executions', label: 'Execution Feed', icon: <Zap className="w-4 h-4" /> },
            { id: 'routes', label: 'Broker Routes', icon: <Globe className="w-4 h-4" /> },
            { id: 'config', label: 'Gateway Config', icon: <Settings className="w-4 h-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                "group flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all duration-200 relative",
                activeTab === tab.id ? "text-white" : "text-slate-400 hover:text-slate-200"
              )}
            >
              <span className={cn(activeTab === tab.id ? "text-orange-400" : "text-slate-500 group-hover:text-slate-300")}>{tab.icon}</span>
              {tab.label}
              {activeTab === tab.id && <motion.div layoutId="emsx-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)]" />}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'orders' && (
            <motion.div key="orders" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <GlassCard className="flex flex-col h-[600px]">
                <div className="p-4 border-b border-white/5 flex items-center justify-between bg-slate-900/30">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                    <input 
                      type="text" 
                      placeholder="Filter by ticker, side or order ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 pr-4 py-2 bg-slate-950/50 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-orange-500/50 transition-all w-80"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button className="px-4 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold rounded-lg transition-all shadow-lg shadow-orange-600/20 uppercase tracking-widest">Route Selected</button>
                  </div>
                </div>
                <div className="flex-1 relative ag-theme-alpine-dark">
                  <AgGridReact
                    rowData={filteredOrders}
                    columnDefs={columnDefs}
                    animateRows={true}
                    pagination={true}
                    paginationPageSize={20}
                    headerHeight={48}
                    rowHeight={44}
                    defaultColDef={{ resizable: true, sortable: true, filter: true }}
                    getRowStyle={(params: any) => ({
                      backgroundColor: params.node.rowIndex % 2 === 0 ? 'rgba(15, 23, 42, 0.3)' : 'transparent',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.03)'
                    })}
                  />
                </div>
              </GlassCard>
            </motion.div>
          )}

          {activeTab === 'executions' && (
            <motion.div key="execs" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-4">
              <GlassCard className="p-6">
                <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2"><History className="w-5 h-5 text-emerald-400" /> Recent Execution Reports</h3>
                <div className="space-y-3">
                  {[
                    { ticker: 'AAPL', qty: 2500, px: 195.28, time: '14:15:02', type: 'PARTIAL' },
                    { ticker: 'MSFT', qty: 1000, px: 378.50, time: '10:45:11', type: 'FILL' },
                    { ticker: 'AAPL', qty: 2500, px: 195.30, time: '12:30:45', type: 'PARTIAL' }
                  ].map((ex, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-slate-950/30 border border-white/5 rounded-2xl">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center text-emerald-400 font-bold text-xs">FILL</div>
                        <div>
                          <div className="text-sm font-bold text-white">{ex.ticker}</div>
                          <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{ex.qty.toLocaleString()} @ ${ex.px.toFixed(2)}</div>
                        </div>
                      </div>
                      <div className="text-right text-xs font-mono text-slate-500">{ex.time}</div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>
          )}

          {activeTab === 'routes' && (
            <motion.div key="routes" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: 'Goldman Sachs', short: 'GSCO', routes: ['SIGMA', 'SIGMA-X', 'REDI'], active: true },
                { name: 'Morgan Stanley', short: 'MSCO', routes: ['MSET', 'MS POOL'], active: true },
                { name: 'JP Morgan', short: 'JPMX', routes: ['LAVA', 'JPMX DMA'], active: true }
              ].map((b, i) => (
                <GlassCard key={i} className="p-6 border border-white/5 hover:border-orange-500/30 transition-all cursor-pointer group">
                  <div className="flex justify-between items-start mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-orange-400 transition-colors"><Globe className="w-6 h-6" /></div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[10px] font-bold uppercase tracking-widest">Active</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{b.name}</h3>
                  <div className="text-xs text-slate-500 font-bold uppercase tracking-widest mb-6">{b.short}</div>
                  <div className="flex flex-wrap gap-2">
                    {b.routes.map(r => <span key={r} className="px-2 py-1 bg-slate-900 border border-white/5 rounded text-[10px] font-mono text-slate-400">{r}</span>)}
                  </div>
                </GlassCard>
              ))}
            </motion.div>
          )}

          {activeTab === 'config' && (
            <motion.div key="config" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <GlassCard className="p-8 max-w-2xl">
                <div className="flex items-center gap-3 mb-8 text-orange-400">
                  <Settings className="w-6 h-6" />
                  <h3 className="text-xl font-bold text-white tracking-tight">Gateway Settings</h3>
                </div>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Environment</label>
                      <select className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500/50 transition-all appearance-none">
                        <option>SIMULATION</option>
                        <option>BETA</option>
                        <option>PRODUCTION</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Heartbeat Interval</label>
                      <input className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500/50 transition-all" defaultValue="5000ms" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">API Session ID</label>
                    <div className="flex gap-3">
                      <input className="flex-1 bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500/50 transition-all font-mono" readOnly value="EMSX-PROD-20241219-0822" />
                      <button className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-all border border-white/5 font-bold text-xs">Reset Session</button>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-white/5">
                    <button className="px-8 py-3 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-orange-600/20 uppercase tracking-widest text-xs">Save Configuration</button>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Container>
  );
};

export default EMSXIntegrationScreen;
